import posthog from 'posthog-js';
import { isInviteSignPath, redactInviteSignUrl } from '../signLink';

const FIRST_TOUCH_STORAGE_KEY = 'mysign_attribution_first_touch';

const SENSITIVE_PATHS = new Set([
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/confirm-email',
  '/document',
]);

export type FirstTouchAttribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  captured_at: string;
};

let initialized = false;
let sessionRecordingPausedForSensitiveRoute = false;
let lastCapturedPageUrl: string | null = null;

const getPostHogKey = (): string => process.env.REACT_APP_POSTHOG_KEY?.trim() ?? '';

const getPostHogHost = (): string => {
  const host = process.env.REACT_APP_POSTHOG_HOST?.trim();
  return host || 'https://b.mysign.az';
};

export const isPostHogEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const enabledFlag = process.env.REACT_APP_POSTHOG_ENABLED?.trim().toLowerCase();
  if (enabledFlag === 'false') {
    return false;
  }
  return getPostHogKey().length > 0;
};

const captureFirstTouchFromUrl = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  if (localStorage.getItem(FIRST_TOUCH_STORAGE_KEY)) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const utm_source = params.get('utm_source')?.trim();
  const utm_medium = params.get('utm_medium')?.trim();
  const utm_campaign = params.get('utm_campaign')?.trim();
  const utm_content = params.get('utm_content')?.trim();
  const utm_term = params.get('utm_term')?.trim();

  if (!utm_source && !utm_medium && !utm_campaign && !utm_content && !utm_term) {
    return;
  }

  const payload: FirstTouchAttribution = {
    captured_at: new Date().toISOString(),
  };
  if (utm_source) payload.utm_source = utm_source;
  if (utm_medium) payload.utm_medium = utm_medium;
  if (utm_campaign) payload.utm_campaign = utm_campaign;
  if (utm_content) payload.utm_content = utm_content;
  if (utm_term) payload.utm_term = utm_term;

  localStorage.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(payload));
};

const getPostHogBootstrapFromUrl = (): { distinctID?: string; sessionID?: string } => {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  const distinctID = params.get('ph_distinct_id')?.trim();
  const sessionID = params.get('ph_session_id')?.trim();
  const bootstrap: { distinctID?: string; sessionID?: string } = {};

  if (distinctID) {
    bootstrap.distinctID = distinctID;
  }
  if (sessionID) {
    bootstrap.sessionID = sessionID;
  }

  return bootstrap;
};

export const getFirstTouchAttribution = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as FirstTouchAttribution;
    const result: Record<string, string> = {};
    if (parsed.utm_source) result.utm_source = parsed.utm_source;
    if (parsed.utm_medium) result.utm_medium = parsed.utm_medium;
    if (parsed.utm_campaign) result.utm_campaign = parsed.utm_campaign;
    if (parsed.utm_content) result.utm_content = parsed.utm_content;
    if (parsed.utm_term) result.utm_term = parsed.utm_term;
    return result;
  } catch {
    return {};
  }
};

export const initPostHog = (): void => {
  if (initialized || !isPostHogEnabled()) {
    return;
  }

  const bootstrap = getPostHogBootstrapFromUrl();

  posthog.init(getPostHogKey(), {
    api_host: getPostHogHost(),
    capture_pageview: false,
    // Manual $pageview on route change; still capture tab close / visibility via beacon.
    capture_pageleave: true,
    autocapture: false,
    session_recording: {
      maskAllInputs: true,
    },
    ...(Object.keys(bootstrap).length > 0 ? { bootstrap } : {}),
  });

  captureFirstTouchFromUrl();
  initialized = true;
};

const isSensitivePath = (pathname: string): boolean =>
  SENSITIVE_PATHS.has(pathname) || isInviteSignPath(pathname);

export const syncSessionRecordingForPath = (pathname: string): void => {
  if (!initialized || !isPostHogEnabled()) {
    return;
  }

  const sensitive = isSensitivePath(pathname);
  if (sensitive) {
    if (!sessionRecordingPausedForSensitiveRoute) {
      posthog.stopSessionRecording();
      sessionRecordingPausedForSensitiveRoute = true;
    }
    return;
  }

  if (sessionRecordingPausedForSensitiveRoute) {
    posthog.startSessionRecording();
    sessionRecordingPausedForSensitiveRoute = false;
  }
};

export const capturePageView = (pathname: string, search: string): void => {
  if (!initialized || !isPostHogEnabled()) {
    return;
  }

  const pathWithSearch = `${pathname}${search}`;
  const currentUrl = redactInviteSignUrl(`${window.location.origin}${pathWithSearch}`);

  if (lastCapturedPageUrl && lastCapturedPageUrl !== currentUrl) {
    posthog.capture('$pageleave', {
      $current_url: lastCapturedPageUrl,
    });
  }

  posthog.capture('$pageview', {
    $current_url: currentUrl,
  });
  lastCapturedPageUrl = currentUrl;
};

export const captureEvent = (
  eventName: string,
  properties?: Record<string, string | number | boolean>
): void => {
  if (!initialized || !isPostHogEnabled()) {
    return;
  }
  posthog.capture(eventName, properties);
};

export const identifyAuthenticatedUser = (
  userId: string,
  personProperties?: Record<string, string>
): void => {
  if (!initialized || !isPostHogEnabled() || !userId.trim()) {
    return;
  }
  posthog.identify(userId, personProperties);
};

export const resetPostHogUser = (): void => {
  if (!initialized || !isPostHogEnabled()) {
    return;
  }
  posthog.reset();
  sessionRecordingPausedForSensitiveRoute = false;
  lastCapturedPageUrl = null;
};
